import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import styles from "./Events.module.css";
import eventImg from "../../../assets/images/about-bg.png";
import api from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import { 
  PartyPopper, Theater, Medal, Users, Briefcase, Wrench, Smile, LayoutGrid,
  Calendar, MapPin, Search, ArrowRight, User, CheckCircle2, Clock, XCircle,
  Phone, Mail, Building2, FileText
} from "lucide-react";

const eventCategories = [
  { name: "All Events", icon: <LayoutGrid size={16} /> },
  { name: "Festival", icon: <PartyPopper size={16} /> },
  { name: "Cultural", icon: <Theater size={16} /> },
  { name: "Sports", icon: <Medal size={16} /> },
  { name: "Community", icon: <Users size={16} /> },
  { name: "Meeting", icon: <Briefcase size={16} /> },
  { name: "Workshop", icon: <Wrench size={16} /> },
  { name: "Kids Activities", icon: <Smile size={16} /> },
];

const Events = () => {
  const navigate = useNavigate();
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All Events");
  const [dynamicCategories, setDynamicCategories] = useState(eventCategories);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [registerEvent, setRegisterEvent] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState('Yes');
  const [memberDetails, setMemberDetails] = useState('');

  const getImageUrl = (path) => {
    if (!path) return eventImg;
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await api.get('/public/events');
        
        const userInfoString = localStorage.getItem('userInfo');
        let userPhone = null;
        if (userInfoString) {
          try {
            const userInfo = JSON.parse(userInfoString);
            userPhone = userInfo.phone || userInfo.mobile;
          } catch (e) {}
        }

        const eventsWithRegistrationStatus = data.map(event => {
          const isRegistered = userPhone && event.externalAttendees?.some(att => att.phone === userPhone);
          return { ...event, registered: isRegistered };
        });

        setEventsData(eventsWithRegistrationStatus);

        const derivedCategories = Array.from(new Set(eventsWithRegistrationStatus.map(e => e.category).filter(Boolean)));
        const existingCatNames = eventCategories.map(c => c.name);
        const newCats = derivedCategories.filter(c => !existingCatNames.includes(c) && c !== 'All Events');
        
        if (newCats.length > 0) {
          const customCatObjects = newCats.map(c => ({ name: c, icon: <PartyPopper size={16} /> }));
          setDynamicCategories([...eventCategories, ...customCatObjects]);
        } else {
          setDynamicCategories(eventCategories);
        }
      } catch (error) {
        console.error("Failed to fetch events", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleRegisterClick = (event) => {
    setRsvp('Yes');
    setMemberDetails('');
    setRegisterEvent(event);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const userInfoString = localStorage.getItem('userInfo');
    if (!userInfoString) {
      setShowLoginPopup(true);
      return;
    }
    
    const userInfo = JSON.parse(userInfoString);

    const formData = {
      fullName: userInfo.fullName || userInfo.name || 'Resident',
      blockFlat: userInfo.flatNo || userInfo.flat || 'N/A',
      phone: userInfo.phone || userInfo.mobile || 'N/A',
      email: userInfo.email || 'N/A',
      attendees: e.target.attendees.value,
      rsvp: rsvp,
      notes: `${memberDetails ? `Member Details: ${memberDetails}\n` : ''}${e.target.notes.value}`
    };

    try {
      setIsSubmitting(true);
      await api.post(`/public/events/${registerEvent._id}/register`, formData);
      
      toast.success(`Successfully registered for ${registerEvent.title}!`);
      
      // Update local state to show as registered
      setEventsData(eventsData.map(ev => 
        ev._id === registerEvent._id ? { ...ev, registered: true } : ev
      ));
      
      setRegisterEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register for event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEvents = eventsData.filter((event) => {
    const matchesCategory = activeCategory === "All Events" || event.category === activeCategory;
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (event.description && event.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = filteredEvents.filter(e => new Date(e.date) >= today);
  const pastEvents = filteredEvents.filter(e => new Date(e.date) < today);

  const renderEventCard = (event, isPast) => {
    const dateObj = new Date(event.date);
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = dateObj.toLocaleString('default', { month: 'short' }).toUpperCase();
    return (
      <div 
        key={event._id} 
        className={styles.eventCard}
        onClick={() => handleRegisterClick(event)}
        style={isPast ? { opacity: 0.8, filter: 'grayscale(30%)', cursor: 'pointer' } : {}}
      >
        <div className={styles.imageArea}>
          <img
            src={getImageUrl(event.image)}
            alt={event.title}
            className={styles.eventImg}
          />
          <div className={styles.categoryBadge}>{event.category || 'General'}</div>
          {isPast && (
            <div className={styles.endedBadge}>Ended</div>
          )}
          <div className={styles.dateBadge}>
            <span className={styles.dateDay}>{day}</span>
            <span className={styles.dateMonth}>{month}</span>
          </div>
        </div>

        <div className={styles.detailsArea}>
          <h3 className={styles.eventTitle}>{event.title}</h3>

          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{event.time || 'TBA'}</span>
            </div>
            <div className={styles.infoItem}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>{event.location || 'TBA'}</span>
            </div>
          </div>

          <p className={styles.eventDescription}>{event.description}</p>

          {!isPast && (
            <button
              className={`${styles.registerBtn} ${event.registered ? styles.registered : ""}`}
              disabled={event.registered}
            >
              {event.registered ? "Registered" : (
                <>Learn More <ArrowRight size={16} /></>
              )}
            </button>
          )}

          <div className={styles.cardBottomFlourish}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c9a35f"
              strokeWidth="1.2"
            >
              <path d="M12 3c-1.5 0-2.5 1.2-2.5 2.5S10.5 8 12 8s2.5-1.2 2.5-2.5S13.5 3 12 3z" />
              <path d="M3 12c0-1.5 1.2-2.5 2.5-2.5S8 10.5 8 12s-1.2 2.5-2.5 2.5S3 13.5 3 12z" />
              <path d="M21 12c0-1.5-1.2-2.5-2.5-2.5S16 10.5 16 12s1.2 2.5 2.5 2.5S21 13.5 21 12z" />
              <circle cx="12" cy="12" r="1.5" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.eventsPage}>
      <div className={styles.headerSection}>
        <svg
          className={styles.headerFeather}
          viewBox="0 0 200 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse
            cx="100"
            cy="60"
            rx="35"
            ry="55"
            stroke="#c9a35f"
            strokeWidth="1.5"
            fill="none"
          />
          <ellipse
            cx="100"
            cy="60"
            rx="18"
            ry="30"
            stroke="#c9a35f"
            strokeWidth="1.5"
            fill="none"
          />
          <line
            x1="100"
            y1="115"
            x2="90"
            y2="480"
            stroke="#c9a35f"
            strokeWidth="1.5"
          />
        </svg>
        <svg
          className={styles.headerCity}
          viewBox="0 0 400 140"
          fill="none"
          stroke="#c9a35f"
          strokeWidth="1.2"
        >
          <rect x="230" y="60" width="50" height="70" fill="none" />
          <rect x="290" y="40" width="55" height="90" fill="none" />
          <rect x="355" y="70" width="35" height="60" fill="none" />
          <path d="M150 130a25 25 0 0 1 25-25 25 25 0 0 1 25 25" fill="none" />
          <path d="M190 130a20 20 0 0 1 20-20 20 20 0 0 1 20 20" fill="none" />
          <path d="M0 130h400" strokeWidth="1" />
        </svg>
        <div className={styles.headerText}>
          <h1 className={styles.pageTitle}>Community Events</h1>
          <div className={styles.headerDivider}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c9a35f"
              strokeWidth="1.2"
            >
              <path d="M12 3c-1.5 0-2.5 1.2-2.5 2.5S10.5 8 12 8s2.5-1.2 2.5-2.5S13.5 3 12 3z" />
              <path d="M12 21c-1.5 0-2.5-1.2-2.5-2.5S10.5 16 12 16s2.5 1.2 2.5 2.5S13.5 21 12 21z" />
              <path d="M3 12c0-1.5 1.2-2.5 2.5-2.5S8 10.5 8 12s-1.2 2.5-2.5 2.5S3 13.5 3 12z" />
              <path d="M21 12c0-1.5-1.2-2.5-2.5-2.5S16 10.5 16 12s1.2 2.5 2.5 2.5S21 13.5 21 12z" />
              <circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <p className={styles.leadText}>
            Discover upcoming and previous events in our society.
          </p>
        </div>
        <div className={styles.headerBottomFlourish}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c9a35f"
            strokeWidth="1.2"
          >
            <path d="M12 3c-1.5 0-2.5 1.2-2.5 2.5S10.5 8 12 8s2.5-1.2 2.5-2.5S13.5 3 12 3z" />
            <path d="M3 12c0-1.5 1.2-2.5 2.5-2.5S8 10.5 8 12s-1.2 2.5-2.5 2.5S3 13.5 3 12z" />
            <path d="M21 12c0-1.5-1.2-2.5-2.5-2.5S16 10.5 16 12s1.2 2.5 2.5 2.5S21 13.5 21 12z" />
            <circle cx="12" cy="12" r="1.5" />
          </svg>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Search Bar UI */}
        <div className={styles.searchContainerWrapper}>
          <div className={styles.searchBar}>
            
            {/* Category Dropdown */}
            <div className={styles.categoryDropdownContainer} ref={dropdownRef}>
              <button 
                className={styles.dropdownToggle}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className={styles.activeCategoryDisplay}>
                  {dynamicCategories.find(c => c.name === activeCategory)?.icon || <LayoutGrid size={18} />}
                  <span>{activeCategory === "All Events" ? "All Categories" : activeCategory}</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {dynamicCategories.map((cat) => (
                    <div 
                      key={cat.name} 
                      className={`${styles.dropdownItem} ${activeCategory === cat.name ? styles.dropdownItemActive : ''}`}
                      onClick={() => {
                        setActiveCategory(cat.name);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className={styles.dropdownItemIcon}>{cat.icon || <PartyPopper size={16} />}</span>
                      <span>{cat.name === "All Events" ? "All Categories" : cat.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className={styles.searchDivider}></div>

            {/* Search Input */}
            <div className={styles.searchInputWrapper}>
              <Search size={20} className={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Search events, festivals, workshops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {/* Search Button */}
            <button className={styles.searchActionBtn}>
              <Search size={18} /> Search
            </button>
          </div>
        </div>

        {/* Events Grid */}
        <div className={styles.eventsGridContainer}>
          {loading ? (
            <p>Loading events...</p>
          ) : (
            <>
              {upcomingEvents.length > 0 && (
                <div className={styles.eventsSection}>
                  <h2 className={styles.sectionHeading}>Upcoming Events</h2>
                  <div className={styles.eventsGrid}>
                    {upcomingEvents.map((event) => renderEventCard(event, false))}
                  </div>
                </div>
              )}

              {pastEvents.length > 0 && (
                <div className={styles.eventsSection} style={{ marginTop: upcomingEvents.length > 0 ? '4rem' : '0' }}>
                  <h2 className={styles.sectionHeading}>Past Events</h2>
                  <div className={styles.eventsGrid}>
                    {pastEvents.map((event) => renderEventCard(event, true))}
                  </div>
                </div>
              )}

              {!loading && filteredEvents.length === 0 && (
                <div className={styles.noEvents} style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                  <h3 style={{ color: '#475569', marginBottom: '8px' }}>No Events Found</h3>
                  <p style={{ color: '#64748b' }}>There are no {activeCategory !== 'All Events' ? activeCategory : ''} events scheduled at the moment.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Registration Modal */}
      {registerEvent && (
        <div
          className={styles.modalOverlay}
          onClick={() => setRegisterEvent(null)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeBtn}
              onClick={() => setRegisterEvent(null)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className={styles.modalBannerSection}>
              {registerEvent.image ? (
                <img src={getImageUrl(registerEvent.image)} alt={registerEvent.title} className={styles.topBannerImg} />
              ) : (
                <div className={styles.topBannerPlaceholder}></div>
              )}
            </div>

            <div className={styles.modalInfoCardWrapper}>
              <div className={styles.modalInfoCard}>
                <div className={styles.infoTopRow}>
                  <div className={styles.infoTitleArea}>
                    <h2>{registerEvent.title}</h2>
                    {registerEvent.description && <p>{registerEvent.description}</p>}
                  </div>
                </div>
                
                <div className={styles.infoBottomRow}>
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxIcon} style={{ color: '#9f1239', backgroundColor: '#ffe4e6' }}>
                      <Calendar size={20} />
                    </div>
                    <div className={styles.infoBoxText}>
                      <span className={styles.infoBoxLabel}>Date</span>
                      <span className={styles.infoBoxValue}>{new Date(registerEvent.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}</span>
                    </div>
                  </div>
                  
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxIcon} style={{ color: '#9f1239', backgroundColor: '#ffe4e6' }}>
                      <Clock size={20} />
                    </div>
                    <div className={styles.infoBoxText}>
                      <span className={styles.infoBoxLabel}>Time</span>
                      <span className={styles.infoBoxValue}>{registerEvent.time}</span>
                    </div>
                  </div>

                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxIcon} style={{ color: '#9f1239', backgroundColor: '#ffe4e6' }}>
                      <MapPin size={20} />
                    </div>
                    <div className={styles.infoBoxText}>
                      <span className={styles.infoBoxLabel}>Location</span>
                      <span className={styles.infoBoxValue}>{registerEvent.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {(new Date(registerEvent.date) < today || registerEvent.registered) && (
            <div style={{ padding: '32px 24px', textAlign: 'center' }}>
              {registerEvent.registered && <p style={{ color: '#059669', fontWeight: '600', marginBottom: '16px', fontSize: '1.1rem' }}>You are already registered for this event.</p>}
              {new Date(registerEvent.date) < today && <p style={{ color: '#64748b', fontWeight: '600', marginBottom: '16px', fontSize: '1.1rem' }}>This event has already ended.</p>}
              <button 
                onClick={() => setRegisterEvent(null)}
                className={styles.cancelBtn}
                style={{ backgroundColor: '#f1f5f9' }}
              >
                Close
              </button>
            </div>
          )}

          {!(new Date(registerEvent.date) < today) && !registerEvent.registered && (
            <form
              className={styles.registrationForm}
              onSubmit={handleFormSubmit}
            >

              <div className={styles.sectionHeader} style={{ marginTop: '1.5rem' }}>
                <Users size={20} className={styles.sectionIcon} />
                <h4>RSVP</h4>
              </div>
              <p className={styles.rsvpSubtitle}>Let us know if you will be attending this event.</p>

              <div className={styles.rsvpCards}>
                <div 
                  className={`${styles.rsvpCard} ${rsvp === 'Yes' ? styles.rsvpActive : ''}`}
                  onClick={() => setRsvp('Yes')}
                >
                  <input type="radio" checked={rsvp === 'Yes'} readOnly className={styles.rsvpRadio} />
                  <div className={styles.rsvpIconBox} style={{ color: '#059669', backgroundColor: '#d1fae5' }}>
                    <CheckCircle2 size={24} />
                  </div>
                  <h5>Yes, I will attend</h5>
                  <p>Looking forward to it!</p>
                </div>
                
                <div 
                  className={`${styles.rsvpCard} ${rsvp === 'Maybe' ? styles.rsvpActive : ''}`}
                  onClick={() => setRsvp('Maybe')}
                >
                  <input type="radio" checked={rsvp === 'Maybe'} readOnly className={styles.rsvpRadio} />
                  <div className={styles.rsvpIconBox} style={{ color: '#d97706', backgroundColor: '#fef3c7' }}>
                    <Clock size={24} />
                  </div>
                  <h5>Maybe</h5>
                  <p>Not sure yet</p>
                </div>

                <div 
                  className={`${styles.rsvpCard} ${rsvp === 'No' ? styles.rsvpActive : ''}`}
                  onClick={() => setRsvp('No')}
                >
                  <input type="radio" checked={rsvp === 'No'} readOnly className={styles.rsvpRadio} />
                  <div className={styles.rsvpIconBox} style={{ color: '#e11d48', backgroundColor: '#ffe4e6' }}>
                    <XCircle size={24} />
                  </div>
                  <h5>No, I will not attend</h5>
                  <p>Sorry, can't make it</p>
                </div>
              </div>

              {rsvp !== 'No' && (
                <>
                  <div className={styles.formRow} style={{ marginTop: '1.5rem' }}>
                <div className={styles.formGroup}>
                  <label htmlFor="attendees">
                    <Users size={16} /> Number of Members Attending <span className={styles.required}>*</span>
                  </label>
                  <select id="attendees" className={styles.selectInput} defaultValue="1">
                    {[...Array(10)].map((_, i) => (
                      <option key={i+1} value={i+1}>{i+1}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="memberDetails">
                    <Users size={16} /> Member Details (Optional)
                  </label>
                  <input
                    type="text"
                    id="memberDetails"
                    value={memberDetails}
                    onChange={(e) => setMemberDetails(e.target.value)}
                    placeholder="e.g. Self, Spouse, Children, Parents"
                  />
                  <span className={styles.inputHint}>You can mention names or relationship (optional)</span>
                </div>
              </div>

              <div className={styles.formGroup} style={{ marginTop: '1.5rem' }}>
                <label htmlFor="notes"><FileText size={16} /> Special Requests / Notes (Optional)</label>
                <textarea
                  id="notes"
                  placeholder="Any special requirements, seating needs, or notes..."
                  rows="2"
                ></textarea>
              </div>
              </>
              )}

              <div className={styles.modalFooter}>
                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => setRegisterEvent(null)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : (rsvp === 'No' ? "Submit" : "Confirm Registration")} {rsvp !== 'No' && <>&rarr;</>}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      )}

      {/* Login Requirement Modal */}
      {showLoginPopup && (
        <div className={styles.modalOverlay} onClick={() => setShowLoginPopup(false)}>
          <div className={styles.modalContent} style={{ maxWidth: '400px', textAlign: 'center', padding: '40px 20px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: '20px', color: '#014d33' }}>
               <User size={48} style={{ margin: '0 auto' }} />
            </div>
            <h3 style={{ fontSize: '22px', marginBottom: '12px', color: '#1e293b' }}>Login Required</h3>
            <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.5' }}>
              You need to be logged in to your account to register for events.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => setShowLoginPopup(false)}
                style={{ padding: '10px 20px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: '500', flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={() => navigate('/login')}
                style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', background: '#014d33', color: '#fff', cursor: 'pointer', fontWeight: '500', flex: 1 }}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
